// ===============================================================================
// ExpenseAnalysis.js - Expense Analysis Page for SplitMon (Premium Feature)
// ===============================================================================
// Location: /var/www/wow/frontend/src/ExpenseAnalysis.js
// Comprehensive expense analytics with charts and insights
// ===============================================================================

import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from "recharts";
import { analysisAPI, groupsAPI, formatCurrency } from "./api";
import "./ExpenseAnalysis.css";

// Chart colors - Forest & Cream theme
const CHART_COLORS = [
  "#2D5A3D", // Forest (primary)
  "#4A7C59", // Forest light
  "#D4A574", // Tan
  "#8FBC8F", // Dark Sea Green
  "#6B8E6B", // Sage
  "#C4A77D", // Tan light
  "#3D6B4F", // Forest medium
  "#A0C49D", // Light green
  "#E8D4B8", // Cream dark
];

// Category display mapping
const CATEGORY_DISPLAY = {
  accommodation: "🏠 Accommodation",
  finance: "💳 Banking & Finance",
  business: "💼 Business Expenses",
  automotive: "🔧 Car Maintenance",
  cleaning: "🧺 Cleaning & Laundry",
  education: "🎓 Education",
  entertainment: "🎬 Entertainment",
  food: "🍕 Food & Drinks",
  fuel: "⛽ Fuel & Petrol",
  garden: "🌳 Garden",
  gifts: "🎁 Gifts & Presents",
  groceries: "🛒 Groceries",
  health: "💊 Health",
  houseexpense: "🏠 House Expenses",
  nightlife: "🎶 Nightlife",
  personalcare: "✂️ Personal Care",
  pets: "🐶 Pet Expenses",
  shopping: "👗 Shopping",
  supplements: "🌿 Supplements",
  subscriptions: "📱 Subscriptions",
  taxes: "🧾 Taxes",
  telecom: "📶 Telecommunication",
  transport: "🚗 Transport",
  travels: "✈️ Travels & Vacations",
  utilities: "💡 Utilities",
  other: "📦 Other"
};

// ===============================================================================
// Number Formatting Helper - Format with commas (e.g., 1,351.67)
// ===============================================================================
const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined || isNaN(num)) return '0.00';
  return Number(num).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

// Helper to extract error message
const getErrorMessage = (err, defaultMessage) => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (Array.isArray(detail)) {
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    if (typeof detail === 'string') return detail;
    if (typeof detail === 'object' && detail.msg) return detail.msg;
  }
  return err?.message || defaultMessage;
};

// Format date for display
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ===============================================================================
// Date Range Presets
// ===============================================================================
const DATE_RANGE_PRESETS = [
  { id: 'project_duration', label: 'Total Project Duration' },
  { id: 'current_year', label: 'Current Year' },
  { id: 'this_month', label: 'Current Month' },
  { id: 'last_12_months', label: 'Last 12 Months' },
  { id: 'last_6_months', label: 'Last 6 Months' },
  { id: 'last_3_months', label: 'Last 3 Months' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'custom', label: 'Custom Range' }, // shown only when user edits dates manually
];

const DEFAULT_PRESET = 'project_duration';

const toISODate = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Compute { dateFrom, dateTo } for a given preset id. Returns null for 'custom'.
const getPresetDateRange = (presetId) => {
  const today = new Date();

  switch (presetId) {
    case 'current_year':
      return { dateFrom: toISODate(new Date(today.getFullYear(), 0, 1)), dateTo: toISODate(today) };
    case 'project_duration':
      return { dateFrom: '1901-01-01', dateTo: toISODate(today) };
    case 'last_6_months':
      return { dateFrom: toISODate(new Date(today.getFullYear(), today.getMonth() - 6, today.getDate())), dateTo: toISODate(today) };
    case 'last_12_months':
      return { dateFrom: toISODate(new Date(today.getFullYear(), today.getMonth() - 12, today.getDate())), dateTo: toISODate(today) };
    case 'last_3_months':
      return { dateFrom: toISODate(new Date(today.getFullYear(), today.getMonth() - 3, today.getDate())), dateTo: toISODate(today) };
    case 'last_month':
      return {
        dateFrom: toISODate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        dateTo: toISODate(new Date(today.getFullYear(), today.getMonth(), 0))
      };
    case 'this_month':
      return { dateFrom: toISODate(new Date(today.getFullYear(), today.getMonth(), 1)), dateTo: toISODate(today) };
    default:
      return null; // 'custom'
  }
};

function ExpenseAnalysis({ user, onBack }) {
  // ===============================================================================
  // State
  // ===============================================================================
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
// Date filters
  const defaultDates = getPresetDateRange(DEFAULT_PRESET);
  const [dateFrom, setDateFrom] = useState(defaultDates.dateFrom);
  const [dateTo, setDateTo] = useState(defaultDates.dateTo);
  const [dateRangePreset, setDateRangePreset] = useState(DEFAULT_PRESET);

  // Group filter
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [showGroupFilter, setShowGroupFilter] = useState(false);

  // Data states
  const [summaryData, setSummaryData] = useState(null);
  const [perPersonData, setPerPersonData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [insightsData, setInsightsData] = useState(null);
  
  // Expanded sections
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedPersons, setExpandedPersons] = useState({});

  // ===============================================================================
  // Load Available Groups on Mount
  // ===============================================================================
  useEffect(() => {
    loadAvailableGroups();
  }, []);

  const loadAvailableGroups = async () => {
    try {
      const res = await groupsAPI.list();
      if (res.data?.ok) {
        setAvailableGroups(res.data.groups || []);
      }
    } catch (err) {
      console.error("Load groups error:", err);
    }
  };

  // ===============================================================================
  // Load Data on Mount and Filter Change
  // ===============================================================================
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    loadAllData();
  }, []);

  // Core fetch — takes explicit filters so it can be called with fresh values
  // immediately after a preset change, without waiting on React state to flush.
  const fetchAnalysisData = useCallback(async (filters) => {
    setLoading(true);
    setError("");

    try {
      const [summaryRes, perPersonRes, trendsRes, insightsRes] = await Promise.all([
        analysisAPI.getSummary(filters),
        analysisAPI.getPerPerson(filters),
        analysisAPI.getTrends(filters),
        analysisAPI.getInsights(filters)
      ]);

      if (summaryRes.data?.ok) setSummaryData(summaryRes.data);
      if (perPersonRes.data?.ok) setPerPersonData(perPersonRes.data);
      if (trendsRes.data?.ok) setTrendsData(trendsRes.data);
      if (insightsRes.data?.ok) setInsightsData(insightsRes.data);

    } catch (err) {
      console.error("Load analysis error:", err);
      setError(getErrorMessage(err, "Failed to load analysis data"));
    }

    setLoading(false);
  }, []);

  // Wrapper that reads current dateFrom/dateTo/selectedGroupIds state —
  // used on mount and by the "Apply Filters" button (manual edits).
  const loadAllData = useCallback(() => {
    return fetchAnalysisData({
      date_from: dateFrom,
      date_to: dateTo,
      group_ids: selectedGroupIds.length > 0 ? selectedGroupIds : null
    });
  }, [dateFrom, dateTo, selectedGroupIds, fetchAnalysisData]);

  const handleApplyFilters = () => {
    loadAllData();
    setShowGroupFilter(false);
  };

  // Preset dropdown handler — computes dates and fetches immediately,
  // rather than waiting for state + the Apply button.
  const handlePresetChange = (e) => {
    const presetId = e.target.value;
    setDateRangePreset(presetId);

    if (presetId === 'custom') return;

    const range = getPresetDateRange(presetId);
    if (!range) return;

    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);

    fetchAnalysisData({
      date_from: range.dateFrom,
      date_to: range.dateTo,
      group_ids: selectedGroupIds.length > 0 ? selectedGroupIds : null
    });
  };

  // If the user edits a date field by hand, drop into "Custom Range" mode
  // so the dropdown accurately reflects that the dates no longer match a preset.
  const handleManualDateChange = (field, value) => {
    if (field === 'from') setDateFrom(value);
    else setDateTo(value);
    setDateRangePreset('custom');
  };

  // ===============================================================================
  // Group Filter Handlers
  // ===============================================================================
  const toggleGroupSelection = (groupId) => {
    setSelectedGroupIds(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const selectAllGroups = () => {
    setSelectedGroupIds(availableGroups.map(g => g.id));
  };

  const clearAllGroups = () => {
    setSelectedGroupIds([]);
  };

  // ===============================================================================
  // Toggle Handlers
  // ===============================================================================
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const toggleCategory = (key) => {
    setExpandedCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const togglePerson = (userId) => {
    setExpandedPersons(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  // ===============================================================================
  // Custom Tooltip for Charts
  // ===============================================================================
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="tooltip-value" style={{ color: entry.color }}>
              {entry.name}: €{formatNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{data.category_display}</p>
          <p className="tooltip-value">€{formatNumber(data.total_amount)}</p>
          <p className="tooltip-percent">{data.percentage}%</p>
        </div>
      );
    }
    return null;
  };

  // ===============================================================================
  // Render Loading
  // ===============================================================================
  if (loading) {
    return (
      <div className="expense-analysis-page">
        <div className="page-loading">
          <div className="spinner"></div>
          <p>Loading analysis...</p>
        </div>
      </div>
    );
  }

  // ===============================================================================
  // Render Error State
  // ===============================================================================
  if (error && !summaryData) {
    return (
      <div className="expense-analysis-page">
        <div className="page-header">
          <div className="header-content">
            <div className="header-title">
              <h1>📊 Expense Analysis</h1>
              <span className="premium-badge">⭐ Premium</span>
            </div>
          </div>
          <button className="back-button" onClick={onBack}>← Back</button>
        </div>
        <div className="error-card">
          <div className="error-icon">⚠️</div>
          <h3>Failed to Load</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadAllData}>Try Again</button>
        </div>
      </div>
    );
  }

  // ===============================================================================
  // Render Overview Tab
  // ===============================================================================
  const renderOverviewTab = () => (
    <div className="tab-content">
      {/* Quick Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <span className="stat-value">€{formatNumber(insightsData?.total_expenses)}</span>
            <span className="stat-label">Total Expenses</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📝</div>
          <div className="stat-content">
            <span className="stat-value">{insightsData?.total_expense_count || 0}</span>
            <span className="stat-label">Transactions</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <span className="stat-value">€{formatNumber(insightsData?.average_expense)}</span>
            <span className="stat-label">Average</span>
          </div>
        </div>
        <div className={`stat-card ${(insightsData?.your_net_balance || 0) >= 0 ? 'positive' : 'negative'}`}>
          <div className="stat-icon">{(insightsData?.your_net_balance || 0) >= 0 ? '📈' : '📉'}</div>
          <div className="stat-content">
            <span className="stat-value">
              {(insightsData?.your_net_balance || 0) >= 0 ? '+' : ''}€{formatNumber(insightsData?.your_net_balance)}
            </span>
            <span className="stat-label">Your Balance</span>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        {/* Monthly Spending Chart */}
        <div className="chart-card">
          <h3>Monthly Spending</h3>
          {trendsData?.monthly_spending?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trendsData.monthly_spending}>
                <defs>
                  <linearGradient id="colorSpending" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2D5A3D" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#2D5A3D" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E0D5" />
                <XAxis dataKey="month_display" tick={{ fill: '#666', fontSize: 12 }} />
                <YAxis tick={{ fill: '#666', fontSize: 12 }} tickFormatter={(v) => `€${v}`} />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="total_amount" 
                  name="Spending"
                  stroke="#2D5A3D" 
                  fillOpacity={1} 
                  fill="url(#colorSpending)" 
                  strokeWidth={3}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-chart-data">No spending data available</div>
          )}
        </div>

        {/* Category Breakdown Pie Chart */}
        <div className="chart-card">
          <h3>Spending by Category</h3>
          {trendsData?.category_totals?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={trendsData.category_totals}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={3}
                  dataKey="total_amount"
                  nameKey="category_display"
                >
                  {trendsData.category_totals.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-chart-data">No category data available</div>
          )}
          {/* Category Legend */}
          <div className="pie-legend">
            {trendsData?.category_totals?.slice(0, 5).map((cat, index) => (
              <div key={cat.category} className="legend-item">
                <span className="legend-color" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}></span>
                <span className="legend-label">{cat.category_display}</span>
                <span className="legend-value">{cat.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Your Summary */}
      <div className="your-summary-card">
        <h3>Your Summary</h3>
        <div className="summary-row">
          <div className="summary-item">
            <span className="summary-label">💵 You Paid</span>
            <span className="summary-value positive">€{formatNumber(insightsData?.your_total_paid)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">💳 You Owe</span>
            <span className="summary-value negative">€{formatNumber(insightsData?.your_total_owed)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">⚖️ Net Balance</span>
            <span className={`summary-value ${(insightsData?.your_net_balance || 0) >= 0 ? 'positive' : 'negative'}`}>
              {(insightsData?.your_net_balance || 0) >= 0 ? '+' : ''}€{formatNumber(insightsData?.your_net_balance)}
            </span>
          </div>
        </div>
      </div>

      {/* Top Expenses */}
      {insightsData?.top_5_expenses?.length > 0 && (
        <div className="top-expenses-card">
          <h3>🏆 Top 5 Expenses</h3>
          <div className="top-expenses-list">
            {insightsData.top_5_expenses.map((exp, index) => (
              <div key={exp.id} className="top-expense-item">
                <div className="expense-rank">#{index + 1}</div>
                <div className="expense-info">
                  <span className="expense-desc">{exp.description}</span>
                  <span className="expense-meta">
                    {exp.group_name} • {CATEGORY_DISPLAY[exp.category] || exp.category} • {exp.paid_by_name}
                  </span>
                </div>
                <div className="expense-amount">€{formatNumber(exp.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Insights */}
      <div className="insights-grid">
        {insightsData?.most_used_category && (
          <div className="insight-card">
            <span className="insight-icon">🏷️</span>
            <div className="insight-content">
              <span className="insight-label">Most Used Category</span>
              <span className="insight-value">{insightsData.most_used_category_display}</span>
              <span className="insight-sub">{insightsData.most_used_category_count} expenses</span>
            </div>
          </div>
        )}
        {insightsData?.most_active_group && (
          <div className="insight-card">
            <span className="insight-icon">👥</span>
            <div className="insight-content">
              <span className="insight-label">Most Active Group</span>
              <span className="insight-value">{insightsData.most_active_group}</span>
              <span className="insight-sub">{insightsData.most_active_group_count} expenses</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ===============================================================================
  // Render Group Breakdown Tab
  // ===============================================================================
  const renderGroupBreakdownTab = () => (
    <div className="tab-content">
      <div className="breakdown-header">
        <h3>Group → Category → Member Breakdown</h3>
        <p className="breakdown-subtitle">
          Total: €{summaryData?.total_all_groups?.toFixed(2) || '0.00'} across {summaryData?.groups?.length || 0} groups
        </p>
      </div>

      {summaryData?.groups?.length === 0 ? (
        <div className="no-data-message">
          <span className="no-data-icon">📭</span>
          <p>No expenses found in the selected date range</p>
        </div>
      ) : (
        <div className="breakdown-list">
          {summaryData?.groups?.map(group => (
            <div key={group.group_id} className="breakdown-group">
              <div 
                className={`group-header-row ${expandedGroups[group.group_id] ? 'expanded' : ''}`}
                onClick={() => toggleGroup(group.group_id)}
              >
                <div className="group-header-left">
                  <span className="expand-icon">{expandedGroups[group.group_id] ? '▼' : '▶'}</span>
                  <span className="group-icon">📁</span>
                  <span className="group-name">{group.group_name}</span>
                  <span className="group-currency-badge">{group.currency}</span>
                </div>
                <div className="group-header-right">
                  <span className="group-count">{group.expense_count} expenses</span>
                  <span className="group-total">€{formatNumber(group.total_amount)}</span>
                </div>
              </div>

              {expandedGroups[group.group_id] && (
                <div className="group-categories">
                  {group.categories?.map(cat => {
                    const catKey = `${group.group_id}-${cat.category}`;
                    return (
                      <div key={catKey} className="category-section">
                        <div 
                          className={`category-header-row ${expandedCategories[catKey] ? 'expanded' : ''}`}
                          onClick={() => toggleCategory(catKey)}
                        >
                          <div className="category-header-left">
                            <span className="expand-icon">{expandedCategories[catKey] ? '▼' : '▶'}</span>
                            <span className="category-name">{cat.category_display}</span>
                          </div>
                          <div className="category-header-right">
                           <span className="category-count">{cat.expense_count} expenses</span>
                            <span className="category-total">€{formatNumber(cat.total_amount)}</span>
                          </div>
                        </div>

                        {expandedCategories[catKey] && (
                          <div className="category-members">
                            {cat.members?.map(member => (
                              <div key={member.user_id} className="member-row">
                                <div className="member-info">
                                  <span className="member-avatar">{member.name?.charAt(0)}</span>
                                  <span className="member-name">{member.name}</span>
                                </div>
                                <div className="member-amounts">
                                  <span className="amount-paid" title="Amount Paid">
                                    💵 €{formatNumber(member.amount_paid)}
                                  </span>
                                  <span className="amount-owed" title="Amount Owed">
                                    💳 €{formatNumber(member.amount_owed)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ===============================================================================
  // Render Per Person Tab
  // ===============================================================================
  const renderPerPersonTab = () => (
    <div className="tab-content">
      <div className="breakdown-header">
        <h3>Total per Person</h3>
        <p className="breakdown-subtitle">
          {perPersonData?.persons?.length || 0} members across your groups
        </p>
      </div>

      {perPersonData?.persons?.length === 0 ? (
        <div className="no-data-message">
          <span className="no-data-icon">📭</span>
          <p>No expense data found for the selected date range</p>
        </div>
      ) : (
        <>
          {/* Per Person Bar Chart */}
          {perPersonData?.persons?.length > 0 && (
            <div className="chart-card full-width">
              <h4>Net Balance by Person</h4>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                  data={perPersonData.persons.slice(0, 10)} 
                  layout="vertical"
                  margin={{ left: 100, right: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E0D5" />
                  <XAxis type="number" tick={{ fill: '#666', fontSize: 12 }} tickFormatter={(v) => `€${v}`} />
                  <YAxis type="category" dataKey="name" tick={{ fill: '#666', fontSize: 12 }} width={90} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="net_balance" 
                    name="Net Balance"
                    fill="#2D5A3D"
                    radius={[0, 4, 4, 0]}
                  >
                    {perPersonData.persons.slice(0, 10).map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.net_balance >= 0 ? '#2D5A3D' : '#D97706'} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Person List */}
          <div className="persons-list">
            {perPersonData?.persons?.map(person => (
              <div key={person.user_id} className="person-card">
                <div 
                  className={`person-header ${expandedPersons[person.user_id] ? 'expanded' : ''}`}
                  onClick={() => togglePerson(person.user_id)}
                >
                  <div className="person-left">
                    <span className="person-avatar">{person.name?.charAt(0)}</span>
                    <div className="person-info">
                      <span className="person-name">{person.name}</span>
                      <span className="person-username">@{person.username}</span>
                    </div>
                  </div>
                  <div className="person-right">
                    <div className="person-stats">
                      <span className="stat-mini paid">Paid: €{formatNumber(person.total_paid)}</span>
                      <span className="stat-mini owed">Owed: €{formatNumber(person.total_owed)}</span>
                    </div>
                    <span className={`person-balance ${person.net_balance >= 0 ? 'positive' : 'negative'}`}>
                      {person.net_balance >= 0 ? '+' : ''}€{formatNumber(person.net_balance)}
                    </span>
                    <span className="expand-icon">{expandedPersons[person.user_id] ? '▲' : '▼'}</span>
                  </div>
                </div>

                {expandedPersons[person.user_id] && (
                  <div className="person-groups">
                    {person.groups?.map(grp => (
                      <div key={grp.group_id} className="person-group-row">
                        <span className="group-name">{grp.group_name}</span>
                        <span className="group-currency">{grp.currency}</span>
                        <span className="group-paid">€{formatNumber(grp.total_paid)}</span>
                        <span className="group-owed">€{formatNumber(grp.total_owed)}</span>
                        <span className={`group-balance ${grp.net_balance >= 0 ? 'positive' : 'negative'}`}>
                          {grp.net_balance >= 0 ? '+' : ''}€{formatNumber(grp.net_balance)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  // ===============================================================================
  // Render Trends Tab
  // ===============================================================================
  const renderTrendsTab = () => (
    <div className="tab-content">
      {/* Monthly Spending Large Chart */}
      <div className="chart-card full-width">
        <h3>Monthly Spending Trend</h3>
        {trendsData?.monthly_spending?.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={trendsData.monthly_spending}>
              <defs>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2D5A3D" />
                  <stop offset="100%" stopColor="#4A7C59" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E0D5" />
              <XAxis dataKey="month_display" tick={{ fill: '#666', fontSize: 12 }} />
              <YAxis tick={{ fill: '#666', fontSize: 12 }} tickFormatter={(v) => `€${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="total_amount" 
                name="Total Spending" 
                fill="url(#barGradient)" 
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="no-chart-data">No monthly data available</div>
        )}
      </div>

      {/* Category Breakdown Detailed */}
      <div className="chart-card full-width">
        <h3>Category Breakdown</h3>
        {trendsData?.category_totals?.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart 
                data={trendsData.category_totals} 
                layout="vertical"
                margin={{ left: 150, right: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E0D5" />
                <XAxis type="number" tick={{ fill: '#666', fontSize: 12 }} tickFormatter={(v) => `€${v}`} />
                <YAxis 
                  type="category" 
                  dataKey="category_display" 
                  tick={{ fill: '#666', fontSize: 12 }} 
                  width={140}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="total_amount" 
                  name="Total"
                  radius={[0, 6, 6, 0]}
                >
                  {trendsData.category_totals.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Category Stats Table */}
            <div className="category-stats-table">
              <div className="table-header">
                <span>Category</span>
                <span>Expenses</span>
                <span>Total</span>
                <span>Percentage</span>
              </div>
              {trendsData.category_totals.map((cat, index) => (
                <div key={cat.category} className="table-row">
                  <span className="cat-name">
                    <span className="cat-color" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}></span>
                    {cat.category_display}
                  </span>
                  <span>{cat.expense_count}</span>
                  <span>€{formatNumber(cat.total_amount)}</span>
                  <span className="cat-percent">{cat.percentage}%</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="no-chart-data">No category data available</div>
        )}
      </div>
    </div>
  );

  // ===============================================================================
  // Main Render
  // ===============================================================================
  return (
    <div className="expense-analysis-page">
      {/* Header */}
      <div className="biz-analysis-header">
        <div className="biz-header-content">
          <div className="biz-header-title">
            <h1>📊 Expense Analysis</h1>
            <span className="biz-premium-badge">⭐ Premium</span>
          </div>
          <p className="biz-header-subtitle">
            Comprehensive insights into your spending patterns
          </p>
        </div>
      </div>

{/* Filters */}
      <div className="biz-filters-card">
        <div className="filters-row">
          <div className="filter-group">
            <label>Quick Range</label>
            <select
              value={dateRangePreset}
              onChange={handlePresetChange}
              className="biz-date-input"
            >
              {DATE_RANGE_PRESETS.map(p => (
                <option key={p.id} value={p.id} hidden={p.id === 'custom'}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>From</label>
            <input 
              type="date" 
              value={dateFrom} 
              onChange={(e) => handleManualDateChange('from', e.target.value)}
              className="biz-date-input"
            />
          </div>
          <div className="filter-group">
            <label>To</label>
            <input 
              type="date" 
              value={dateTo} 
              onChange={(e) => handleManualDateChange('to', e.target.value)}
              className="biz-date-input"
            />
          </div>
          <div className="filter-group">
            <label>Groups</label>
            <button 
              className="biz-group-filter-btn"
              onClick={() => setShowGroupFilter(!showGroupFilter)}
            >
              {selectedGroupIds.length === 0 
                ? 'All Groups' 
                : `${selectedGroupIds.length} Selected`}
              <span className="dropdown-icon">▼</span>
            </button>
          </div>
          <button className="biz-btn-primary" onClick={handleApplyFilters}>
            Apply Filters
          </button>
        </div>

        {/* Group Filter Dropdown */}
        {showGroupFilter && (
          <div className="biz-group-filter-dropdown">
            <div className="dropdown-header">
              <span>Select Groups</span>
              <div className="dropdown-actions">
                <button onClick={selectAllGroups}>Select All</button>
                <button onClick={clearAllGroups}>Clear All</button>
              </div>
            </div>
            <div className="dropdown-list">
              {availableGroups.map(group => (
                <label key={group.id} className="group-option">
                  <input 
                    type="checkbox"
                    checked={selectedGroupIds.includes(group.id)}
                    onChange={() => toggleGroupSelection(group.id)}
                  />
                  <span className="group-icon">{group.name?.charAt(0)?.toUpperCase()}</span>
                  <span className="group-name">{group.name}</span>
                  <span className="group-type">{group.currency}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <p className="date-range-display">
          Showing data from {formatDateDisplay(dateFrom)} to {formatDateDisplay(dateTo)}
          {selectedGroupIds.length > 0 && ` • ${selectedGroupIds.length} group(s) selected`}
        </p>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError("")}>✕</button>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            <span className="tab-icon">📈</span>
            <span className="tab-text">Overview</span>
          </button>
          <button 
            className={`tab ${activeTab === "breakdown" ? "active" : ""}`}
            onClick={() => setActiveTab("breakdown")}
          >
            <span className="tab-icon">📁</span>
            <span className="tab-text">Group Breakdown</span>
          </button>
          <button 
            className={`tab ${activeTab === "per-person" ? "active" : ""}`}
            onClick={() => setActiveTab("per-person")}
          >
            <span className="tab-icon">👥</span>
            <span className="tab-text">Per Person</span>
          </button>
          <button 
            className={`tab ${activeTab === "trends" ? "active" : ""}`}
            onClick={() => setActiveTab("trends")}
          >
            <span className="tab-icon">📊</span>
            <span className="tab-text">Trends</span>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && renderOverviewTab()}
      {activeTab === "breakdown" && renderGroupBreakdownTab()}
      {activeTab === "per-person" && renderPerPersonTab()}
      {activeTab === "trends" && renderTrendsTab()}
    </div>
  );
}

export default ExpenseAnalysis;

